/**
 * Build-time metadata mapping each instrumented package (orchestrion
 * `module.name`) to the channel-subscriber integration that consumes the
 * channels injected into it — by the `exportName` it is published under from
 * `@sentry/server-utils/orchestrion`.
 *
 * Kept in a separate, factory-free module on purpose: the subscribe-injection
 * transform (reachable from every orchestrion bundler plugin) reads this to
 * generate the tiny snippet it injects into each instrumented file, and must
 * not drag any subscriber code — or its `@sentry/core` span machinery — into
 * the plugin's own build to do so.
 *
 * `exportName` must be a named export of `@sentry/server-utils/orchestrion`.
 * `modules` must match `module.name` values in `SENTRY_INSTRUMENTATIONS` — e.g.
 * `postgresChannelIntegration` covers both `pg` and `pg-pool`, and
 * `redisChannelIntegration` both `redis` and `@redis/client`.
 *
 * `redis`, `ioredis` and `dataloader` are included even though they're not in
 * the node SDK's `channelIntegrations` (they only partially replace an OTel
 * integration there): in a bundler-only runtime like Cloudflare Workers there
 * is no OTel integration to coordinate with, so subscribing whenever the
 * package is bundled is unconditionally correct.
 */
export const CHANNEL_INTEGRATION_DEFINITIONS = [
  { exportName: 'postgresChannelIntegration', modules: ['pg', 'pg-pool'] },
  { exportName: 'postgresJsChannelIntegration', modules: ['postgres'] },
  { exportName: 'mysqlChannelIntegration', modules: ['mysql'] },
  { exportName: 'mysql2ChannelIntegration', modules: ['mysql2'] },
  { exportName: 'genericPoolChannelIntegration', modules: ['generic-pool'] },
  { exportName: 'lruMemoizerChannelIntegration', modules: ['lru-memoizer'] },
  { exportName: 'openaiChannelIntegration', modules: ['openai'] },
  { exportName: 'anthropicChannelIntegration', modules: ['@anthropic-ai/sdk'] },
  { exportName: 'googleGenAIChannelIntegration', modules: ['@google/genai'] },
  { exportName: 'vercelAiChannelIntegration', modules: ['ai'] },
  { exportName: 'amqplibChannelIntegration', modules: ['amqplib'] },
  { exportName: 'hapiChannelIntegration', modules: ['@hapi/hapi'] },
  { exportName: 'expressChannelIntegration', modules: ['express', 'router'] },
  { exportName: 'graphqlChannelIntegration', modules: ['graphql'] },
  { exportName: 'kafkajsChannelIntegration', modules: ['kafkajs'] },
  { exportName: 'redisChannelIntegration', modules: ['redis', '@redis/client'] },
  { exportName: 'ioredisChannelIntegration', modules: ['ioredis'] },
  { exportName: 'dataloaderChannelIntegration', modules: ['dataloader'] },
] as const satisfies ReadonlyArray<{ exportName: string; modules: readonly string[] }>;

/** Look up the subscriber export name for an instrumented package, if any. */
export function subscriberExportForModule(moduleName: string): string | undefined {
  return CHANNEL_INTEGRATION_DEFINITIONS.find(d => (d.modules as readonly string[]).includes(moduleName))?.exportName;
}
